import React from 'react';

import type { EmojiProvider } from '@atlaskit/emoji/resource';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	AVP_DASHBOARD_CREATE_KEY,
	isAvpPreviewCardAction,
} from '@atlassian/conversation-assistant-action-helpers/utils/avp-actions';
import {
	CONFLUENCE_AIFC_DATABASE_CREATE_KEY,
	CONFLUENCE_AIFC_SLIDES_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	isConfluencePageCreateAction,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { isJiraCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jira-actions';
import { isJsmCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jsm-actions';
import { SmartCreatePreviewCard } from '@atlassian/conversation-assistant-preview-card/conversation-assistant-preview-card';
import type { MessageActionResolutions } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { ContentType } from '@atlassian/conversation-assistant-store/controllers/preview-content/contentType';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';

const getProductFromActionKey = (actionKey: string) => {
	if (isJiraCreatableAction({ key: actionKey })) {
		return 'jira';
	}

	if (isJsmCreatableAction({ key: actionKey })) {
		return 'jsm';
	}

	if (isAvpPreviewCardAction({ key: actionKey })) {
		return 'avp';
	}

	// Ideally this isn't the default case, but it's a fallback for now
	return 'confluence';
};

const getContentType = (key: string) => {
	if (isConfluencePageCreateAction({ key })) {
		return ContentType.liveDoc;
	}
	if (key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY) {
		return ContentType.whiteboard;
	}
	if (key === CONFLUENCE_AIFC_SLIDES_CREATE_KEY) {
		return ContentType.slides;
	}
	if (key === CONFLUENCE_AIFC_DATABASE_CREATE_KEY) {
		return ContentType.database;
	}
	if (key === AVP_DASHBOARD_CREATE_KEY) {
		return ContentType.dashboard;
	}
	return null;
};

// Resolves whether this preview card is the one currently being viewed.
//
// Legacy (single-create / experiment off): the whole message is selected, so either message-level
// selection matches or the card's own invocation is the selected one.
//
// Multi-create (experiment on): a card selected by `invocationId` is the only one viewing. When
// selection is message-level (e.g. auto-open) the preview resolves to the first creatable action,
// so only the first card claims it — otherwise every card in the message would appear selected and
// become unclickable.
export const getIsPreviewCardSelected = ({
	isMultiCreateEnabled,
	messageId,
	messageIdSelectedForPreview,
	invocationId,
	invocationIdSelectedForPreview,
	isFirstPreviewCard,
}: {
	isMultiCreateEnabled: boolean;
	messageId: string;
	messageIdSelectedForPreview: string | null;
	invocationId: string;
	invocationIdSelectedForPreview: string | null;
	isFirstPreviewCard: boolean;
}): boolean => {
	if (!isMultiCreateEnabled) {
		return (
			messageIdSelectedForPreview === messageId ||
			(!!invocationIdSelectedForPreview && invocationId === invocationIdSelectedForPreview)
		);
	}

	if (invocationIdSelectedForPreview) {
		return invocationId === invocationIdSelectedForPreview;
	}

	return messageIdSelectedForPreview === messageId && isFirstPreviewCard;
};

export const SmartCreatePreviewComponent = ({
	title: actionTitle,
	contentId: actionContentId,
	actionKey,
	messageId,
	emojiProvider,
	stagingAreaEnabled,
	fullScreen,
	conversationId,
	status,
	cloudId,
	shouldOpenPreviewInAtlassianHome,
	isLastMessage,
	invocationId,
	resolveActions,
	resolveAction,
	product,
	showSuccessFlagOnCreate,
	isFirstPreviewCard = true,
}: {
	title: string | undefined;
	contentId: string;
	actionKey: string;
	messageId: string;
	emojiProvider?: Promise<EmojiProvider>;
	stagingAreaEnabled?: boolean;
	fullScreen?: boolean;
	conversationId?: string;
	status?: string;
	cloudId: string;
	shouldOpenPreviewInAtlassianHome?: boolean;
	isLastMessage: boolean;
	invocationId: string;
	resolveActions?: (
		actionKey: string,
		actionResolution: MessageActionResolutions,
		invocationIds: string[],
	) => void;
	resolveAction?: (
		actionKey: string,
		actionResolution: MessageActionResolutions,
		invocationId: string,
	) => void;
	product?: string;
	/**
	 * Controls whether a success flag is shown after work items are created.
	 * Defaults to `true`. Surfaces such as rovo-chat-desktop can pass `false`
	 * to suppress the AFM success flag and surface their own confirmation.
	 */
	showSuccessFlagOnCreate?: boolean;
	/**
	 * First previewable card in its message. Only the first card claims message-level selection;
	 * the others select by `invocationId`. Defaults to `true` for single-card usages.
	 */
	isFirstPreviewCard?: boolean;
}): React.JSX.Element => {
	const previewProduct = getProductFromActionKey(actionKey);
	const { messageIdSelectedForPreview, invocationIdSelectedForPreview } = useStagingAreaState();
	const isSmartCreateModalOpen = messageIdSelectedForPreview !== null;
	const isPreviewCardEnabled = !isSmartCreateModalOpen && isLastMessage;

	// Selection is conversation-wide (`invocationIdSelectedForPreview` is a single value), so
	// invocationId-based selection must apply whenever the experiment is on, including single-action
	// messages. No-exposure read so this per-render lookup does not log an experiment exposure.
	const isMultiCreateEnabled = FeatureGates.getExperimentValue<boolean>(
		'cwr-be-based-multi-creation-v0',
		'isEnabled',
		false,
	);

	const isSelected = getIsPreviewCardSelected({
		isMultiCreateEnabled,
		messageId,
		messageIdSelectedForPreview,
		invocationId,
		invocationIdSelectedForPreview,
		isFirstPreviewCard,
	});

	return (
		<SmartCreatePreviewCard
			previewProduct={previewProduct}
			product={product}
			contentType={getContentType(actionKey)}
			title={actionTitle ?? ''}
			isSelected={isSelected}
			isEnabled={isPreviewCardEnabled}
			isLastMessage={isLastMessage}
			contentId={actionContentId}
			emojiProvider={emojiProvider}
			stagingAreaEnabled={stagingAreaEnabled}
			fullScreen={fullScreen}
			conversationId={conversationId}
			messageId={messageId}
			status={status}
			cloudId={cloudId}
			shouldOpenPreviewInAtlassianHome={shouldOpenPreviewInAtlassianHome}
			resolveActions={resolveActions}
			resolveAction={resolveAction}
			invocationId={invocationId}
			showSuccessFlagOnCreate={showSuccessFlagOnCreate}
		/>
	);
};
